import { supabase } from '@/lib/supabase/client';
import { BlogPost, BlogCategory, BlogTag, BlogPostWithRelations } from '@/types/models';
import { Database } from '@/types/database.types';

type BlogPostRow = Database['public']['Tables']['blog_posts']['Row'];
type BlogCategoryRow = Database['public']['Tables']['blog_categories']['Row'];
type BlogTagRow = Database['public']['Tables']['blog_tags']['Row'];

// Case conversion helpers
const convertPostToCamelCase = (post: BlogPostRow): BlogPost => ({
  id: post.id,
  title: post.title,
  slug: post.slug,
  excerpt: post.excerpt,
  content: post.content,
  featuredImage: post.featured_image,
  authorId: post.author_id,
  categoryId: post.category_id,
  status: post.status,
  readTime: post.read_time,
  viewCount: post.view_count,
  publishedAt: post.published_at,
  createdAt: post.created_at,
  updatedAt: post.updated_at,
  metaTitle: post.meta_title,
  metaDescription: post.meta_description,
  socialImage: post.social_image,
});

const convertCategoryToCamelCase = (category: BlogCategoryRow): BlogCategory => ({
  id: category.id,
  name: category.name,
  slug: category.slug,
  description: category.description,
  categoryType: category.category_type,
  createdAt: category.created_at,
  updatedAt: category.updated_at,
});

const convertTagToCamelCase = (tag: BlogTagRow): BlogTag => ({
  id: tag.id,
  name: tag.name,
  slug: tag.slug,
  createdAt: tag.created_at,
});

export const blogService = {
  // Get all published posts with optional filtering
  async getAllPublishedPosts(options?: {
    categorySlug?: string;
    tagSlug?: string;
    searchQuery?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: BlogPostWithRelations[] | null; error: Error | null; count: number | null }> {
    try {
      let query = supabase
        .from('blog_posts')
        .select(`
          *,
          author:user_profiles!blog_posts_author_id_fkey(id, full_name, avatar_url),
          category:blog_categories!blog_posts_category_id_fkey(*),
          post_tags!inner(
            tag:blog_tags(*)
          )
        `, { count: 'exact' })
        .eq('status', 'published')
        .order('published_at', { ascending: false });

      // Apply filters
      if (options?.categorySlug) {
        query = query.eq('category.slug', options.categorySlug);
      }

      if (options?.tagSlug) {
        query = query.eq('post_tags.tag.slug', options.tagSlug);
      }

      if (options?.searchQuery) {
        query = query.or(`title.ilike.%${options.searchQuery}%,excerpt.ilike.%${options.searchQuery}%`);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      // Transform data to camelCase with relations
      const transformedData = data?.map((post: any) => ({
        ...convertPostToCamelCase(post),
        author: post.author ? {
          id: post.author.id,
          fullName: post.author.full_name,
          avatarUrl: post.author.avatar_url,
        } : undefined,
        category: post.category ? convertCategoryToCamelCase(post.category) : null,
        tags: post.post_tags?.map((pt: any) => convertTagToCamelCase(pt.tag)) || [],
      })) || [];

      return { data: transformedData as BlogPostWithRelations[], error: null, count };
    } catch (error) {
      return { data: null, error: error as Error, count: null };
    }
  },

  // Get single post by slug
  async getPostBySlug(slug: string): Promise<{ data: BlogPostWithRelations | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          *,
          author:user_profiles!blog_posts_author_id_fkey(id, full_name, avatar_url),
          category:blog_categories!blog_posts_category_id_fkey(*),
          post_tags(
            tag:blog_tags(*)
          )
        `)
        .eq('slug', slug)
        .eq('status', 'published')
        .single();

      if (error) throw error;

      if (!data) {
        return { data: null, error: new Error('Post not found') };
      }

      // Transform to camelCase with relations
      const transformedPost: BlogPostWithRelations = {
        ...convertPostToCamelCase(data),
        author: {
          id: data.author.id,
          fullName: data.author.full_name,
          avatarUrl: data.author.avatar_url,
        },
        category: data.category ? convertCategoryToCamelCase(data.category) : null,
        tags: data.post_tags?.map((pt: any) => convertTagToCamelCase(pt.tag)) || [],
      };

      return { data: transformedPost, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get all categories
  async getAllCategories(): Promise<{ data: BlogCategory[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('blog_categories')
        .select('*')
        .order('name');

      if (error) throw error;

      const transformedData = data?.map(convertCategoryToCamelCase) || [];
      return { data: transformedData, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get all tags
  async getAllTags(): Promise<{ data: BlogTag[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('blog_tags')
        .select('*')
        .order('name');

      if (error) throw error;

      const transformedData = data?.map(convertTagToCamelCase) || [];
      return { data: transformedData, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Increment view count
  async incrementViewCount(postId: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.rpc('increment', {
        row_id: postId,
        table_name: 'blog_posts',
        column_name: 'view_count',
      });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Search posts
  async searchPosts(query: string): Promise<{ data: BlogPostWithRelations[] | null; error: Error | null }> {
    return this.getAllPublishedPosts({ searchQuery: query, limit: 20 });
  },

  // Get featured posts (most viewed)
  async getFeaturedPosts(limit: number = 3): Promise<{ data: BlogPost[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'published')
        .order('view_count', { ascending: false })
        .limit(limit);

      if (error) throw error;

      const transformedData = data?.map(convertPostToCamelCase) || [];
      return { data: transformedData, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get recent posts
  async getRecentPosts(limit: number = 5): Promise<{ data: BlogPost[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      const transformedData = data?.map(convertPostToCamelCase) || [];
      return { data: transformedData, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  // Get related posts (same category or tags)
  async getRelatedPosts(postId: string, limit: number = 3): Promise<{ data: BlogPost[] | null; error: Error | null }> {
    try {
      // First get the current post's category and tags
      const { data: currentPost, error: postError } = await supabase
        .from('blog_posts')
        .select('category_id, post_tags(tag_id)')
        .eq('id', postId)
        .single();

      if (postError) throw postError;

      // Get related posts by category
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'published')
        .neq('id', postId)
        .eq('category_id', currentPost.category_id)
        .order('published_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      const transformedData = data?.map(convertPostToCamelCase) || [];
      return { data: transformedData, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },
};